import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AuthContext from '../contexts/AuthContext';
import FollowList from '../components/follow/FollowList';
import {
  FollowUser,
  listenToFollowers,
  listenToFollowing,
  followUser,
  unfollowUser
} from '../services/followService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

interface UserData {
  uid: string;
  displayName: string;
  photoURL: string;
  bio?: string;
}

type FollowParams = {
  userId: string;
  type: 'followers' | 'following';
};

const FollowListPage: React.FC = () => {
  const { userId } = useParams<FollowParams>();
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();

  const [users, setUsers] = useState<FollowUser[]>([]);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async (): Promise<(() => void) | undefined> => {
      if (!userId) {
        setError('ID de usuario no válido');
        setLoading(false);
        return undefined;
      }

      try {
        // Obtener datos del usuario
        const userDocRef = doc(db, 'users', userId);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data() as UserData;
          setUserData({
            uid: userId,
            displayName: userData.displayName || 'Usuario',
            photoURL: userData.photoURL || '/default-avatar.png',
            bio: userData.bio
          });
        } else {
          setError('Usuario no encontrado');
          setLoading(false);
          return undefined;
        }

        // Configurar listener para seguidores o seguidos
        let unsubscribe: () => void;

        // Determinar si estamos viendo seguidores o seguidos basado en la URL
        const isFollowers = window.location.pathname.includes('/followers');
        const isFollowing = window.location.pathname.includes('/following');

        if (isFollowers) {
          unsubscribe = listenToFollowers(userId, currentUser?.uid, (followers) => {
            setUsers(followers);
            setLoading(false);
          });
        } else if (isFollowing) {
          unsubscribe = listenToFollowing(userId, currentUser?.uid, (following) => {
            setUsers(following);
            setLoading(false);
          });
        } else {
          // Por defecto, mostrar seguidores
          unsubscribe = listenToFollowers(userId, currentUser?.uid, (followers) => {
            setUsers(followers);
            setLoading(false);
          });
        }

        // Limpiar listener cuando el componente se desmonte
        return () => unsubscribe();
      } catch (error) {
        console.error(`Error fetching followers/following:`, error);
        setError(`Error al cargar la lista de seguidores/seguidos`);
        setLoading(false);
        return undefined;
      }
    };

    fetchUserData();
  }, [userId, currentUser]);

  const handleFollowToggle = async (targetUserId: string) => {
    if (!currentUser) return;

    try {
      // Encontrar el usuario a actualizar
      const userToUpdate = users.find(user => user.uid === targetUserId);

      if (!userToUpdate) {
        console.error('Usuario no encontrado');
        return;
      }

      // Guardar el estado actual de seguimiento
      const isCurrentlyFollowing = userToUpdate.isFollowing;

      // Actualizar el estado localmente usando el patrón funcional
      // Esto evita problemas de sincronización con el DOM virtual de React
      setUsers(prevUsers => {
        return prevUsers.map(user =>
          user.uid === targetUserId
            ? { ...user, isFollowing: !isCurrentlyFollowing }
            : user
        );
      });

      // Realizar la operación en la base de datos
      if (isCurrentlyFollowing) {
        await unfollowUser(currentUser.uid, targetUserId);
      } else {
        await followUser(
          currentUser.uid,
          targetUserId,
          {
            displayName: currentUser.displayName || 'Usuario',
            photoURL: currentUser.photoURL
          },
          {
            displayName: userToUpdate.displayName,
            photoURL: userToUpdate.photoURL
          }
        );
      }
    } catch (error) {
      console.error('Error toggling follow status:', error);
      alert('Error al actualizar estado de seguimiento. Inténtalo de nuevo.');

      // En caso de error, revertir el cambio local
      setUsers(prevUsers => [...prevUsers]);
    }
  };

  if (error) {
    return (
      <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow">
        <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300">Error</h2>
        <p className="text-gray-500 dark:text-gray-400 mt-2">{error}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 px-4 py-2 bg-domired-600 text-white rounded-md hover:bg-domired-700"
        >
          Volver
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden p-4">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(-1)}
          className="mr-2 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Volver"
        >
          <ArrowLeftIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {window.location.pathname.includes('/followers') ? 'Seguidores' : 'Siguiendo'}
          </h1>
          {userData && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {window.location.pathname.includes('/followers')
                ? `Personas que siguen a ${userData.displayName}`
                : `Personas a las que ${userData.displayName} sigue`}
            </p>
          )}
        </div>
      </div>

      <FollowList
        users={users}
        loading={loading}
        emptyMessage={window.location.pathname.includes('/followers')
          ? 'Este usuario aún no tiene seguidores.'
          : 'Este usuario aún no sigue a nadie.'}
        onFollowToggle={handleFollowToggle}
      />
    </div>
  );
};

export default FollowListPage;
